import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useLocation, useSearchParams } from 'react-router-dom';
import { ArtworkGrid } from '../components/artwork';
import { ErrorBoundary, ArtworkLightbox } from '../components/ui';
import { ArtworkResponse, ArtworkFilters, artworkService, tagService } from '../services';

const PAGE_SIZE = 20;

const GalleryContainer = styled.div`
  padding: ${({ theme }) => `${theme.spacing.xl} 0 ${theme.spacing['3xl']}`};
  background: ${({ theme }) => theme.colors.gallery.background};

  @media (max-width: 640px) {
    padding: ${({ theme }) => `${theme.spacing.md} 0 ${theme.spacing['3xl']}`};
  }
`;

const Header = styled.header`
  display: grid;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  text-align: center;

  @media (max-width: 640px) {
    gap: ${({ theme }) => theme.spacing.xs};
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
`;

const Title = styled.h1`
  margin: 0;
  color: ${({ theme }) => theme.colors.gallery.text.primary};
  font-family: ${({ theme }) => theme.typography.fontFamily.gallery.display};
  font-size: ${({ theme }) => theme.typography.fontSize.gallery.hero};
  font-weight: ${({ theme }) => theme.typography.fontWeight.gallery.light};
  line-height: 1.1;

  @media (max-width: 640px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  max-width: 42rem;
  margin: 0 auto;
  color: ${({ theme }) => theme.colors.gallery.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.gallery.metadata};
  line-height: 1.6;

  @media (max-width: 640px) {
    max-width: 21rem;
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    line-height: 1.4;
  }
`;

const Filters = styled.section`
  display: grid;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  padding: ${({ theme }) => theme.spacing.lg};
  border: 1px solid ${({ theme }) => theme.colors.gallery.border.subtle};
  border-radius: ${({ theme }) => theme.borderRadius.gallery.soft};
  background: ${({ theme }) => theme.colors.gallery.surface};

  @media (max-width: 640px) {
    gap: ${({ theme }) => theme.spacing.sm};
    margin-bottom: ${({ theme }) => theme.spacing.md};
    padding: ${({ theme }) => theme.spacing.md};
  }
`;

const SearchForm = styled.form`
  display: flex;
  align-items: end;
  gap: ${({ theme }) => theme.spacing.sm};

  @media (max-width: 640px) {
    display: grid;
  }
`;

const SearchLabel = styled.label`
  display: grid;
  flex: 1;
  gap: ${({ theme }) => theme.spacing.xs};
  color: ${({ theme }) => theme.colors.gallery.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const SearchInput = styled.input`
  min-height: 44px;
  padding: ${({ theme }) => `0 ${theme.spacing.md}`};
  border: 1px solid ${({ theme }) => theme.colors.gallery.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.gallery.soft};
  background: ${({ theme }) => theme.colors.gallery.surfaceRaised};
  color: ${({ theme }) => theme.colors.gallery.text.primary};
  font: inherit;

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.semantic.focus};
    outline-offset: 2px;
  }
`;

const FilterButton = styled.button`
  min-height: 44px;
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.lg}`};
  border: 1px solid ${({ theme }) => theme.colors.gallery.accent.border};
  border-radius: ${({ theme }) => theme.borderRadius.gallery.soft};
  background: ${({ theme }) => theme.colors.gallery.accent.primary};
  color: ${({ theme }) => theme.colors.gallery.text.inverse};
  font: inherit;
  font-weight: ${({ theme }) => theme.typography.fontWeight.gallery.medium};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.gallery.accent.hover};
  }

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.semantic.focus};
    outline-offset: 2px;
  }
`;

const SecondaryButton = styled(FilterButton)`
  background: transparent;
  color: ${({ theme }) => theme.colors.gallery.text.primary};

  &:hover {
    background: ${({ theme }) => theme.colors.gallery.surfaceRaised};
  }
`;

const Disclosure = styled.button`
  display: none;
  min-height: 44px;
  border: 1px solid ${({ theme }) => theme.colors.gallery.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.gallery.soft};
  background: transparent;
  color: ${({ theme }) => theme.colors.gallery.text.primary};
  font: inherit;
  cursor: pointer;

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.semantic.focus};
    outline-offset: 2px;
  }

  @media (max-width: 640px) {
    display: block;
  }
`;

const FilterOptions = styled.div<{ $open: boolean }>`
  display: grid;
  gap: ${({ theme }) => theme.spacing.sm};

  @media (max-width: 640px) {
    display: ${({ $open }) => ($open ? 'grid' : 'none')};
  }
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const TagButton = styled.button<{ $selected: boolean }>`
  min-height: 40px;
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  border: 1px solid ${({ theme, $selected }) => $selected ? theme.colors.gallery.accent.primary : theme.colors.gallery.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme, $selected }) => $selected ? theme.colors.gallery.accent.primary : 'transparent'};
  color: ${({ theme }) => theme.colors.gallery.text.primary};
  font: inherit;
  cursor: pointer;

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.semantic.focus};
    outline-offset: 2px;
  }
`;

const ActiveFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs};
  align-items: center;
  color: ${({ theme }) => theme.colors.gallery.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const ActiveFilter = styled.span`
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  border: 1px solid ${({ theme }) => theme.colors.gallery.border.subtle};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.gallery.surfaceRaised};
`;

const TagsError = styled.div`
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => `${theme.colors.error}66`};
  border-radius: ${({ theme }) => theme.borderRadius.gallery.soft};
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const TagsRetry = styled.button`
  min-height: 40px;
  margin-top: ${({ theme }) => theme.spacing.sm};
  padding: 0 ${({ theme }) => theme.spacing.md};
  border: 1px solid currentColor;
  border-radius: ${({ theme }) => theme.borderRadius.gallery.soft};
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
`;

export interface GalleryPageProps {
  galleryMode?: boolean;
}

interface GalleryHistoryContext {
  queryString: string;
  scrollY: number;
  focusArtworkId: number;
}

interface GalleryLocationState {
  galleryContext?: GalleryHistoryContext;
}

function getFilters(searchParams: URLSearchParams): ArtworkFilters {
  const search = searchParams.get('search')?.trim();
  const tags = (searchParams.get('tags') || '').split(',').map((tag) => tag.trim()).filter(Boolean);
  return {
    ...(search ? { search } : {}),
    ...(tags.length ? { tags } : {}),
  };
}

export function GalleryPage({ galleryMode = false }: GalleryPageProps = {}) {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => getFilters(searchParams), [searchParams]);
  const queryString = searchParams.toString();
  const [searchInput, setSearchInput] = useState(filters.search || '');
  const [showFilters, setShowFilters] = useState(false);
  const restoredHistoryEntry = useRef<string | null>(null);

  useEffect(() => {
    setSearchInput(filters.search || '');
  }, [filters.search]);

  const tagsQuery = useQuery({
    queryKey: ['tags', 'popular'],
    queryFn: () => tagService.getPopularTags(20),
    retry: false,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchOnReconnect: 'always',
  });

  const artworksQuery = useInfiniteQuery({
    queryKey: ['gallery-artworks', filters],
    queryFn: ({ pageParam = 1 }) => artworkService.getArtworks(pageParam, PAGE_SIZE, filters),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    retry: false,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchOnReconnect: 'always',
    staleTime: 0,
  });

  const hasArtworkPageError = artworksQuery.isFetchNextPageError;
  const publicArtworksReady = artworksQuery.isFetchedAfterMount && artworksQuery.data !== undefined &&
    artworksQuery.fetchStatus !== 'paused' && !artworksQuery.isRefetching && !artworksQuery.isRefetchError &&
    (artworksQuery.isSuccess || hasArtworkPageError);
  const artworks = useMemo(
    () => publicArtworksReady ? (artworksQuery.data?.pages.flatMap((page) => page.artworks) || []) : [],
    [artworksQuery.data, publicArtworksReady],
  );
  const totalCount = publicArtworksReady ? (artworksQuery.data?.pages[0]?.total ?? 0) : 0;
  const selectedTags = filters.tags || [];
  const hasFilters = Boolean(filters.search || selectedTags.length);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedArtwork, setSelectedArtwork] = useState<ArtworkResponse | null>(null);
  const [currentArtworkIndex, setCurrentArtworkIndex] = useState(0);
  const [artworksForLightbox, setArtworksForLightbox] = useState<ArtworkResponse[]>([]);
  const [lightboxEntry, setLightboxEntry] = useState<{ pathname: string; queryString: string } | null>(null);
  const lightboxOpenerRef = useRef<HTMLElement | null>(null);

  const historyContext = (location.state as GalleryLocationState | null)?.galleryContext;

  useEffect(() => {
    if (lightboxOpen || !historyContext || historyContext.queryString !== queryString || restoredHistoryEntry.current === location.key) {
      return;
    }
    if (artworks.length === 0) return;

    const frame = window.requestAnimationFrame(() => {
      window.scrollTo({ top: historyContext.scrollY, behavior: 'auto' });
      const card = document.querySelector<HTMLElement>(`[data-artwork-id="${historyContext.focusArtworkId}"]`);
      card?.focus({ preventScroll: true });
      restoredHistoryEntry.current = location.key;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [artworks.length, historyContext, lightboxOpen, location.key, queryString]);

  const setGalleryFilters = (next: { search?: string; tags?: string[] }) => {
    const nextParams = new URLSearchParams();
    if (next.search?.trim()) nextParams.set('search', next.search.trim());
    if (next.tags?.length) nextParams.set('tags', next.tags.join(','));
    setSearchParams(nextParams);
  };

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setGalleryFilters({ search: searchInput, tags: selectedTags });
  };

  const toggleTag = (tagName: string) => {
    const nextTags = selectedTags.includes(tagName)
      ? selectedTags.filter((tag) => tag !== tagName)
      : [...selectedTags, tagName];
    setGalleryFilters({ search: filters.search, tags: nextTags });
  };

  const clearFilters = () => {
    setSearchInput('');
    setGalleryFilters({});
  };

  const openLightbox = (artwork: ArtworkResponse, activatingElement?: HTMLElement) => {
    const index = artworks.findIndex((item) => item.artwork_id === artwork.artwork_id);
    if (index < 0) return;
    lightboxOpenerRef.current = activatingElement ?? (document.activeElement as HTMLElement | null);
    setSelectedArtwork(artwork);
    setCurrentArtworkIndex(index);
    setArtworksForLightbox(artworks);
    setLightboxEntry({ pathname: location.pathname, queryString });
    setLightboxOpen(true);
  };

  const rememberBrowsingEntry = (artwork: ArtworkResponse) => {
    const currentHistoryState = window.history.state;
    const currentUserState = currentHistoryState && typeof currentHistoryState.usr === 'object'
      ? currentHistoryState.usr
      : {};
    window.history.replaceState(
      {
        ...currentHistoryState,
        usr: {
          ...currentUserState,
          galleryContext: {
            queryString,
            scrollY: window.scrollY,
            focusArtworkId: artwork.artwork_id,
          } satisfies GalleryHistoryContext,
        },
      },
      '',
      window.location.href,
    );
  };

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    setSelectedArtwork(null);
    setArtworksForLightbox([]);
    setLightboxEntry(null);
  }, []);

  useEffect(() => {
    if (artworksQuery.isRefetching || artworksQuery.isRefetchError || !publicArtworksReady) closeLightbox();
  }, [artworksQuery.isRefetchError, artworksQuery.isRefetching, closeLightbox, publicArtworksReady]);

  useEffect(() => {
    if (!lightboxOpen || !lightboxEntry) return;
    if (lightboxEntry.pathname !== location.pathname || lightboxEntry.queryString !== queryString) {
      closeLightbox();
    }
  }, [closeLightbox, lightboxEntry, lightboxOpen, location.pathname, queryString]);

  useEffect(() => {
    if (!lightboxOpen || !lightboxEntry || lightboxEntry.pathname !== location.pathname || lightboxEntry.queryString !== queryString || !selectedArtwork) return;
    const nextIndex = artworks.findIndex((item) => item.artwork_id === selectedArtwork.artwork_id);
    if (nextIndex < 0) {
      closeLightbox();
      return;
    }
    if (artworksForLightbox !== artworks) setArtworksForLightbox(artworks);
    if (currentArtworkIndex !== nextIndex) setCurrentArtworkIndex(nextIndex);
    const nextArtwork = artworks[nextIndex];
    if (nextArtwork !== selectedArtwork) setSelectedArtwork(nextArtwork);
  }, [artworks, artworksForLightbox, closeLightbox, currentArtworkIndex, lightboxEntry, lightboxOpen, location.pathname, queryString, selectedArtwork]);

  const navigateLightbox = (direction: 'prev' | 'next') => {
    const nextIndex = direction === 'prev' ? currentArtworkIndex - 1 : currentArtworkIndex + 1;
    if (nextIndex >= 0 && nextIndex < artworksForLightbox.length) {
      setCurrentArtworkIndex(nextIndex);
      setSelectedArtwork(artworksForLightbox[nextIndex]);
    }
  };

  const publicTagsReady = tagsQuery.isFetchedAfterMount && tagsQuery.isSuccess && !tagsQuery.isError && !tagsQuery.isRefetching && tagsQuery.fetchStatus !== 'paused';
  // Readiness controls whether tag data can be rendered. Error state must be
  // handled separately, otherwise an initial/refetch failure can never reach
  // the retry UI because readiness is necessarily false on failure.
  const tagsError = tagsQuery.isError && !tagsQuery.isRefetching && tagsQuery.fetchStatus !== 'paused'
    ? tagsQuery.error
    : null;
  const artworkError = artworksQuery.error;
  const tagsErrorMessage = tagsQuery.isError && artworksQuery.isSuccess && !artworkError
    ? 'Tag filtering is temporarily unavailable. The current artwork results are still available.'
    : 'Tag filtering and artwork results are currently unavailable. Please try again.';

  return (
    <ErrorBoundary>
      <GalleryContainer data-gallery-mode={galleryMode || undefined}>
        <div className="container">
          <Header>
            <Title>Student Gallery</Title>
              <Subtitle>Discover beautiful artwork created by students at Joanne Art Class. Search by student or filter by tag.</Subtitle>
          </Header>

          <Filters aria-label="Gallery filters">
            <SearchForm onSubmit={handleSearchSubmit}>
              <SearchLabel htmlFor="gallery-search">
                Search the gallery
                <SearchInput
                  id="gallery-search"
                  type="search"
                  placeholder="Search by student name or keyword"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                />
              </SearchLabel>
              <FilterButton type="submit">Search</FilterButton>
              {hasFilters && <SecondaryButton type="button" onClick={clearFilters}>Clear all</SecondaryButton>}
            </SearchForm>

            <Disclosure
              type="button"
              aria-expanded={showFilters}
              aria-controls="gallery-tag-options"
              onClick={() => setShowFilters((open) => !open)}
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Disclosure>
            <FilterOptions id="gallery-tag-options" $open={showFilters}>
              {tagsError ? (
                <TagsError role="status">
                  {tagsErrorMessage}
                  <br />
                  <TagsRetry type="button" onClick={() => void tagsQuery.refetch()} disabled={tagsQuery.isFetching}>
                    {tagsQuery.isFetching ? 'Trying again…' : 'Try tags again'}
                  </TagsRetry>
                </TagsError>
              ) : publicTagsReady ? (
                <Tags>
                  {tagsQuery.data?.map((tag) => (
                    <TagButton
                      key={tag.tag_id}
                      type="button"
                      $selected={selectedTags.includes(tag.tag_name)}
                      aria-pressed={selectedTags.includes(tag.tag_name)}
                      onClick={() => toggleTag(tag.tag_name)}
                    >
                      {tag.tag_name} ({tag.usage_count})
                    </TagButton>
                  ))}
                </Tags>
              ) : tagsQuery.isFetching ? (
                <TagsError role="status">Updating filters…</TagsError>
              ) : null}
            </FilterOptions>
            {hasFilters && (
              <ActiveFilters aria-label="Active gallery filters">
                <span>Active filters:</span>
                {filters.search && <ActiveFilter>Search: {filters.search}</ActiveFilter>}
                {selectedTags.map((tag) => <ActiveFilter key={tag}>Tag: {tag}</ActiveFilter>)}
              </ActiveFilters>
            )}
          </Filters>

          <ArtworkGrid
            artworks={artworks}
            totalCount={totalCount}
            filters={filters}
            isLoading={artworksQuery.isLoading || (!publicArtworksReady && (artworksQuery.isFetching || artworksQuery.fetchStatus === 'paused'))}
            isError={artworksQuery.isError || artworksQuery.isRefetchError}
            error={artworkError}
            onRetry={async () => { await artworksQuery.refetch(); }}
            hasNextPage={artworksQuery.hasNextPage}
            isFetchingNextPage={artworksQuery.isFetchingNextPage}
            isFetchNextPageError={artworksQuery.isFetchNextPageError}
            isFetching={artworksQuery.isFetching}
            onLoadMore={async () => { await artworksQuery.fetchNextPage(); }}
            onArtworkClick={openLightbox}
            onClearFilters={clearFilters}
            columns={4}
            aspectRatio={4 / 5}
            showPortfolioLinks
            onPortfolioNavigate={rememberBrowsingEntry}
          />

          <ArtworkLightbox
            artwork={selectedArtwork}
            artworks={artworksForLightbox}
            currentIndex={currentArtworkIndex}
            isOpen={lightboxOpen}
            onClose={closeLightbox}
            onNavigate={navigateLightbox}
            activatingElement={lightboxOpenerRef.current}
            onPortfolioNavigate={rememberBrowsingEntry}
          />
        </div>
      </GalleryContainer>
    </ErrorBoundary>
  );
}
